import React from 'react';
import Head from 'next/head';
import Router, { useRouter } from 'next/router';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { ErrorBoundary } from 'next/dist/client/components/error-boundary';
import { toast } from 'react-toastify';
import { Concurrency } from '@cogitate/diep2-components-core';

import MyApp from './myApp';
import {
  store,
  reactPlugin,
  sendExceptionLogs,
  heartbeatLock,
  lockPolicy,
  releaseLock,
} from '@cogitate/ui-multi-utils';
import { currentLineLob } from 'ui/utilities/helper';
import { LockWhenPolicyIdReady, hasSubmittedPolicyId } from 'ui/components/concurrency/LockWhenPolicyIdReady';
import { storeKeys, commonKeys, apolloKeys, routeKeys, requestKeys, buttonRouteKeys } from 'ui/data/constants/keys';
import {
  ignoreRoutesHeartbeart,
  ignoreRoutesReleaseLock,
  normalizeConcurrencyPath,
} from 'ui/data/constants/concurrencyRoutes';

const lockPolicyAfterSubmit = async (policy, needApiResponse = false) => {
  if (!hasSubmittedPolicyId(policy)) {
    return needApiResponse ? null : true;
  }
  return lockPolicy(policy, needApiResponse);
};

const heartbeatLockAfterSubmit = async (policy) => {
  if (!hasSubmittedPolicyId(policy)) {
    return;
  }
  return heartbeatLock(policy);
};

const releaseLockAfterSubmit = async (policy) => {
  if (!hasSubmittedPolicyId(policy)) {
    return;
  }
  return releaseLock(policy);
};

const WrappedApp = ({ Component, pageProps }) => {
  const router = useRouter();

  return (
    <>
      <Head>
        <base href={process.env.NEXT_PUBLIC_BASE_PATH + '/'} />
      </Head>
      <Provider store={store}>
        <AppInsightsContext.Provider value={reactPlugin}>
          <ErrorBoundary
            FallbackComponent={(error, _resetErrorBoundary) => {
              const defaultErrorText = 'Oops something went wrong. Try going back to the login page';
              const errorText =
                process.env.NEXT_PUBLIC_DISPLAY_ERROR === true
                  ? error.error !== undefined
                    ? JSON.stringify(error, 2)
                    : defaultErrorText
                  : error.error.message;
              const logData = {
                app: 'UI',
                errorMessage: error.error.message,
                stacktrace: error.error,
                url: window.location.href,
                client: `${process.env.NEXT_PUBLIC_CLIENT}`,
                Lob: `${currentLineLob()}`,
                input: {},
                functionName: '',
                severityLevel: 'Exception',
              };
              console.error({ error }, errorText);
              sendExceptionLogs(logData);
            }}
          >
            <PersistGate persistor={store.__PERSISTOR} loading={null}>
              <Concurrency
                store={store}
                keys={{ storeKeys, commonKeys, apolloKeys, routeKeys, requestKeys, buttonRouteKeys }}
                router={router}
                pathname={normalizeConcurrencyPath(router.asPath)}
                toast={toast}
                useSelector={useSelector}
                uiMultiUtils={{
                  heartbeatLock: heartbeatLockAfterSubmit,
                  releaseLock: releaseLockAfterSubmit,
                  lockPolicy: lockPolicyAfterSubmit,
                }}
                Router={Router}
                ignoreRoutesHeartbeart={ignoreRoutesHeartbeart}
                ignoreRoutesReleaseLock={ignoreRoutesReleaseLock}
                idleWarningMs={5 * 60 * 1000}
                heartbeatMs={60000}
              >
                <LockWhenPolicyIdReady />
                <MyApp Component={Component} pageProps={pageProps} />
              </Concurrency>
            </PersistGate>
          </ErrorBoundary>
        </AppInsightsContext.Provider>
      </Provider>
    </>
  );
};
export default WrappedApp;
